import type { ChatStatus, LiveConversation } from '@manaro/contracts'
import { cn } from '@/shared/lib/cn.ts'
import { formatTime } from '@/shared/lib/format.ts'
import { Avatar } from '@/shared/ui/avatar.tsx'
import { dayLabel } from '../chat-format.ts'

export const STATUS_LABELS: Record<ChatStatus, string> = {
  bot: 'Отвечает бот',
  waiting_operator: 'Ждёт оператора',
  operator: 'У оператора',
  closed: 'Закрыт',
}

const STATUS_DOT: Record<ChatStatus, string> = {
  bot: 'bg-ink-faint',
  waiting_operator: 'bg-warn-dot',
  operator: 'bg-brand',
  closed: '',
}

export const visitorLabel = (item: LiveConversation): string => item.visitorName ?? item.visitorEmail ?? 'Гость'

function lastActivity(iso: string): string {
  const day = dayLabel(iso)
  return day === 'Сегодня' ? formatTime(iso) : day
}

export function StatusAvatar({ item, size = 'md' }: { item: LiveConversation; size?: 'md' | 'sm' }) {
  return (
    <span className="relative shrink-0">
      <Avatar
        name={visitorLabel(item)}
        className={size === 'md' ? 'size-12 text-[15px]' : 'size-9 text-[13px]'}
      />
      {item.status !== 'closed' && (
        <span
          aria-hidden
          className={cn(
            'absolute right-0 bottom-0 size-3.5 rounded-full border-2 border-surface',
            STATUS_DOT[item.status],
          )}
        />
      )}
    </span>
  )
}

interface ConversationListItemProps {
  item: LiveConversation
  active: boolean
  onSelect: () => void
}

/** One chat in the list, the way messengers show it: who, what was said last, when, and how much is unread. */
export function ConversationListItem({ item, active, onSelect }: ConversationListItemProps) {
  const unread = item.unread > 0 && !active
  const waiting = item.status === 'waiting_operator'

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active || undefined}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors md:px-5',
        active ? 'bg-brand-tint' : 'active:bg-surface-hover md:hover:bg-[#fcfbf7]',
      )}
    >
      <StatusAvatar item={item} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 border-b border-line-soft pb-3 [button:last-child_&]:border-b-0">
        <span className="flex items-baseline gap-2">
          <span className={cn('min-w-0 flex-1 truncate text-[15.5px] tracking-[-0.005em]', unread ? 'font-semibold' : 'font-medium')}>
            {visitorLabel(item)}
          </span>
          <span className={cn('tabular shrink-0 text-[12.5px]', unread ? 'font-semibold text-brand' : 'text-ink-muted')}>
            {lastActivity(item.lastActivityAt)}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <span className={cn('line-clamp-2 min-w-0 flex-1 text-[14px] leading-snug', unread ? 'text-ink' : 'text-ink-muted')}>
            {item.lastMessage ?? STATUS_LABELS[item.status]}
          </span>
          {unread && (
            <span className="tabular min-w-[22px] shrink-0 rounded-full bg-brand px-1.5 text-center text-[12px] leading-[22px] font-semibold text-white">
              {item.unread}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 truncate text-[12.5px] text-ink-muted">
          {waiting ? (
            <span className="font-medium text-warn-ink">Ждёт оператора</span>
          ) : item.operatorName && item.status === 'operator' ? (
            <span>ведёт {item.operatorName}</span>
          ) : null}
          {item.businessTitle && (
            <>
              {(waiting || (item.operatorName && item.status === 'operator')) && <span aria-hidden>·</span>}
              <span className="truncate">{item.businessTitle}</span>
            </>
          )}
        </span>
      </span>
    </button>
  )
}
